import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { AgentLeaderboardFilter, AgentLeaderboardResponse } from './agent-leaderboard.models';

// Agent DTOs based on Swagger definitions
export interface AgentDto {
  id: string;
  agentId: string;
  name: string;
  phoneNumber: string;
  zones: string[];
  isActive: boolean;
  lastSelfieUploadedAt?: string;
  pricingGroupId?: string;
  createdAt?: string;
  completedCollectionCount?: number;
  profilePictureUpdatedAt?: string | null;
}

export interface CreateAgentDto {
  name: string;
  phoneNumber: string;
}

export interface AssignAgentZoneDto {
  zoneId: string;
  agentId: string;
}

export interface UpdateAgentStatusDto {
  agentId: string;
  isActive: boolean;
  notes?: string;
  updatedBy: string;
}

export interface AgentDtoPaginatedResponse {
  items: AgentDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface EntityIdResponseDto {
  id: string;
}

export interface ZoneDto {
  id: string;
  name: string;
  disabled?: string;
  deletedAt?: string;
  updatedAt: string;
  createdAt: string;
  districtId?: string | null;
  coordinate?: {
    type: string;
    coordinates: {
      latitude: number;
      longitude: number;
    }[];
  };
}

export interface SubscriptionDetail {
  subscriptionId: string;
  type: string;
  cadence: string;
  amount: number;
  baseAmount: number;
  discountAmount: number;
  status: string;
  startDate: string;
  endDate: string;
  paymentStatus: string;
}

export interface ScrapItemBreakdown {
  scrapItemId: string;
  scrapItemName: string;
  image: string;
  unit: string;
  totalQuantity: number;
  totalDeductedQuantity: number;
  netQuantity: number;
  totalValue: number;
  totalMarketValue: number;
  profitValue: number;
  profitPercentage: number;
  collectionCount: number;
}

export interface AgentSelfieCheckinDto {
  agentId: string;
  name: string;
  phoneNumber: string;
  zones: string[];
  hasCheckedIn: boolean;
  checkedInAt?: string;
}

export interface AgentSelfieUrlDto {
  agentId: string;
  selfieUrl: string;
  date: string;
}

export interface AgentBusinessReport {
  agentId: string;
  agentName: string;
  month: number;
  year: number;
  monthName: string;
  totalPickups: number;
  completedPickups: number;
  cancelledAppointments: number;
  rejectedAppointments: number;
  totalScrapVolumeCollected: number;
  totalBusinessValue: number;
  totalMarketValue: number;
  totalGrandTotal: number;
  totalPlatformCharges: number;
  totalCollectionCharges: number;
  profitValue: number;
  profitPercentage: number;
  totalSubscriptionFees: number;
  activeSubscriptionsCount: number;
  subscriptionDetails: SubscriptionDetail[];
  averageRating: number;
  totalRatings: number;
  feedbackCount: number;
  customerFeedbacks: string[];
  scrapItemBreakdown: ScrapItemBreakdown[];
  reportStartDate: string;
  reportEndDate: string;
}

@Injectable({
  providedIn: 'root'
})
export class AgentService {
  
  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) { }

  private getUpdatedByEmail(): string {
    const activeAccount = this.authService.getActiveAccount();
    const idTokenClaims = (activeAccount as any)?.idTokenClaims;
    const emailFromClaims = idTokenClaims?.email || idTokenClaims?.preferred_username || idTokenClaims?.upn;
    
    return activeAccount?.username || 
           emailFromClaims ||
           activeAccount?.localAccountId || 
           activeAccount?.homeAccountId?.split('.')[0] || 
           activeAccount?.name || 
           'Unknown';
  }

  /**
   * Create a new agent
   * POST /api/Agent/create
   */
  createAgent(agentData: CreateAgentDto): Observable<EntityIdResponseDto> {
    return this.http.post<EntityIdResponseDto>(
      `${environment.apiConfig.uri}/api/Agent/create`, 
      agentData
    );
  }

  /**
   * Assign zone to an agent
   * POST /api/Agent/assign-zone
   */
  assignAgentZone(assignData: AssignAgentZoneDto): Observable<EntityIdResponseDto> {
    return this.http.post<EntityIdResponseDto>(
      `${environment.apiConfig.uri}/api/Agent/assign-zone`, 
      assignData
    );
  }

  /**
   * Remove zone from an agent
   * POST /api/Agent/remove-zone
   */
  removeAgentZone(removeData: AssignAgentZoneDto): Observable<any> {
    return this.http.post<any>(
      `${environment.apiConfig.uri}/api/Agent/remove-zone`, 
      removeData
    );
  }

  /**
   * Assign pricing group to an agent
   * POST /api/Agent/assign-pricing-group
   */
  assignAgentPricingGroup(assignData: { agentId: string; pricingGroupId: string }): Observable<EntityIdResponseDto> {
    return this.http.post<EntityIdResponseDto>(
      `${environment.apiConfig.uri}/api/Agent/assign-pricing-group`, 
      assignData
    );
  }

  /**
   * Remove pricing group from an agent
   * POST /api/Agent/remove-pricing-group/{agentId}
   */
  removeAgentPricingGroup(agentId: string): Observable<any> {
    return this.http.post<any>(
      `${environment.apiConfig.uri}/api/Agent/remove-pricing-group/${agentId}`, 
      {}
    );
  }

  /**
   * Update agent status
   * PATCH /api/Agent/status
   */
  updateAgentStatus(statusData: UpdateAgentStatusDto): Observable<AgentDto> {
    return this.http.patch<AgentDto>(
      `${environment.apiConfig.uri}/api/Agent/status`, 
      statusData
    );
  }

  /**
   * Get list of agents with optional filters
   * GET /api/Agent/list
   */
  getAgentList(
    zoneId?: string, 
    isActive?: boolean, 
    pageIndex: number = 0, 
    pageSize: number = 10
  ): Observable<AgentDtoPaginatedResponse> {
    let params: any = {
      PageIndex: pageIndex,
      PageSize: pageSize
    };

    if (zoneId) {
      params.ZoneId = zoneId;
    }

    if (isActive !== undefined) {
      params.IsActive = isActive;
    }

    const queryString = Object.keys(params)
      .map(key => `${key}=${encodeURIComponent(params[key])}`)
      .join('&');

    return this.http.get<AgentDtoPaginatedResponse>(
      `${environment.apiConfig.uri}/api/Agent/list?${queryString}`
    );
  }

  /**
   * Get agents by zone ID
   * GET /api/Agent/zone/{zoneId}
   */
  getAgentsByZone(
    zoneId: string, 
    pageIndex: number = 0, 
    pageSize: number = 10
  ): Observable<AgentDtoPaginatedResponse> {
    return this.http.get<AgentDtoPaginatedResponse>(
      `${environment.apiConfig.uri}/api/Agent/zone/${zoneId}?pageIndex=${pageIndex}&pageSize=${pageSize}`
    );
  }

  /**
   * Enable an agent
   * POST /api/Agent/enable/{agentId}
   */
  enableAgent(agentId: string, notes?: string): Observable<AgentDto> {
    const statusData: UpdateAgentStatusDto = {
      agentId: agentId,
      isActive: true,
      notes: notes,
      updatedBy: this.getUpdatedByEmail()
    };

    return this.http.post<AgentDto>(
      `${environment.apiConfig.uri}/api/Agent/enable/${agentId}`, 
      statusData
    );
  }

  /**
   * Disable an agent
   * POST /api/Agent/disable/{agentId}
   */
  disableAgent(agentId: string, notes?: string): Observable<AgentDto> {
    const statusData: UpdateAgentStatusDto = {
      agentId: agentId,
      isActive: false,
      notes: notes,
      updatedBy: this.getUpdatedByEmail()
    };

    return this.http.post<AgentDto>(
      `${environment.apiConfig.uri}/api/Agent/disable/${agentId}`, 
      statusData
    );
  }

  /**
   * Get all zones for zone assignment
   * GET /api/GeoZone/list
   */
  getAllZones(): Observable<ZoneDto[]> {
    return this.http.get<ZoneDto[]>(`${environment.apiConfig.uri}/api/GeoZone/list`);
  }

  /**
   * Get zones assigned to a specific agent
   * GET /api/GeoZone/list/{agentId}
   */
  getAgentZones(agentId: string): Observable<ZoneDto[]> {
    return this.http.get<ZoneDto[]>(`${environment.apiConfig.uri}/api/GeoZone/list/${agentId}`);
  }

  /**
   * Toggle agent status (enable/disable)
   */
  toggleAgentStatus(agent: AgentDto, notes?: string): Observable<AgentDto> {
    if (agent.isActive) {
      return this.disableAgent(agent.agentId, notes);
    } else {
      return this.enableAgent(agent.agentId, notes);
    }
  }

  /**
   * Helper method to get agents for dropdown/selection
   */
  getAgentsForSelection(): Observable<{ value: string; label: string }[]> {
    return this.getAgentList(undefined, true, 0, 100).pipe(
      map(response => response.items.map(agent => ({
        value: agent.agentId,
        label: `${agent.name} (${agent.phoneNumber})`
      })))
    );
  }

  /**
   * Helper method to get zones for dropdown/selection
   */
  getZonesForSelection(): Observable<{ value: string; label: string }[]> {
    return this.getAllZones().pipe(
      map(zones => zones.map(zone => ({
        value: zone.id,
        label: zone.name
      })))
    );
  }

  /**
   * Helper method to get zones for the district/zone filter, keeping districtId
   */
  getZonesForDistrictFilter(): Observable<{ value: string; label: string; districtId: string | null }[]> {
    return this.getAllZones().pipe(
      map(zones => zones.map(zone => ({
        value: zone.id,
        label: zone.name,
        districtId: zone.districtId ?? null
      })))
    );
  }

  /**
   * Get monthly business report for an agent
   * GET /api/ScrapCollection/agent/monthly-business-report
   */
  getAgentMonthlyBusinessReport(
    agentId: string,
    month: number,
    year: number
  ): Observable<AgentBusinessReport> {
    return this.http.get<AgentBusinessReport>(
      `${environment.apiConfig.uri}/api/ScrapCollection/agent/monthly-business-report?agentId=${agentId}&month=${month}&year=${year}`
    );
  }

  /**
   * Get selfie check-in status for all active agents for a given date.
   * GET /api/Agent/selfie-checkins
   */
  getSelfieCheckIns(date?: string, zoneId?: string): Observable<AgentSelfieCheckinDto[]> {
    const params: Record<string, string> = {};
    if (date) params['date'] = date;
    if (zoneId) params['zoneId'] = zoneId;
    const query = new URLSearchParams(params).toString();
    return this.http.get<AgentSelfieCheckinDto[]>(
      `${environment.apiConfig.uri}/api/Agent/selfie-checkins${query ? '?' + query : ''}`
    );
  }

  /**
   * Get a short-lived SAS URL for a specific agent's selfie on the given date.
   * GET /api/Agent/{agentId}/selfie-url
   */
  getAgentSelfieUrl(agentId: string, date?: string): Observable<AgentSelfieUrlDto> {
    const params: Record<string, string> = {};
    if (date) params['date'] = date;
    const query = new URLSearchParams(params).toString();
    return this.http.get<AgentSelfieUrlDto>(
      `${environment.apiConfig.uri}/api/Agent/${agentId}/selfie-url${query ? '?' + query : ''}`
    );
  }

  /**
   * Get an agent by ID
   * GET /api/Agent/{agentId}
   */
  getAgentById(agentId: string): Observable<AgentDto> {
    return this.http.get<AgentDto>(
      `${environment.apiConfig.uri}/api/Agent/${agentId}`
    );
  }

  /**
   * Override/Approve the daily selfie check-in for a specific agent and date.
   * POST /api/Agent/{agentId}/selfie-override
   */
  overrideAgentSelfie(agentId: string, date?: string): Observable<void> {
    const params: Record<string, string> = {};
    if (date) params['date'] = date;
    const query = new URLSearchParams(params).toString();
    return this.http.post<void>(
      `${environment.apiConfig.uri}/api/Agent/${agentId}/selfie-override${query ? '?' + query : ''}`,
      {}
    );
  }

  /**
   * Get agent performance leaderboard and voucher conversion metrics
   * GET /api/Agent/leaderboard
   */
  getAgentLeaderboard(filter: AgentLeaderboardFilter): Observable<AgentLeaderboardResponse> {
    const params: Record<string, string> = {};
    if (filter.fromDate) params['fromDate'] = filter.fromDate;
    if (filter.toDate) params['toDate'] = filter.toDate;
    if (filter.geoZoneIds) params['geoZoneIds'] = filter.geoZoneIds;
    if (filter.agentIds) params['agentIds'] = filter.agentIds;
    const query = new URLSearchParams(params).toString();
    return this.http.get<AgentLeaderboardResponse>(
      `${environment.apiConfig.uri}/api/Agent/leaderboard${query ? '?' + query : ''}`
    );
  }
}

